import React from "react";
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Stack,
  Chip,
  Avatar,
} from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faDownload,
  faPrint,
  faEnvelope,
  faPhone,
  faLocationDot,
} from "@fortawesome/free-solid-svg-icons";
import { faGithub, faWhatsapp } from "@fortawesome/free-brands-svg-icons";
import {
  CONTACT,
  PROFILE,
  EXPERIENCE,
  EDUCATION,
  SKILLS,
  openWhatsApp,
} from "../../utils/constants";
import projectsData from "../../data/projects";

const Resume = () => {
  const featuredProjects = projectsData.slice(0, 4);

  return (
    <Box sx={{ py: 2 }}>
      <Paper
        elevation={0}
        sx={{
          borderRadius: 4,
          border: "1px solid",
          borderColor: "divider",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            p: { xs: 3, md: 4 },
            color: "white",
            background: "linear-gradient(135deg, #0f172a, #2563eb)",
          }}
        >
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md="auto">
              <Avatar
                src={CONTACT.profileImage}
                alt={CONTACT.fullName}
                imgProps={{ loading: "eager", referrerPolicy: "no-referrer" }}
                sx={{
                  width: 150,
                  height: 150,
                  border: "4px solid rgba(255,255,255,0.85)",
                  boxShadow: "0 10px 25px rgba(0,0,0,0.2)",
                  mx: { xs: "auto", md: 0 },
                }}
              />
            </Grid>

            <Grid item xs={12} md>
              <Typography variant="h3" sx={{ fontWeight: 800 }}>
                {CONTACT.fullName}
              </Typography>
              <Typography variant="h6" sx={{ opacity: 0.95, mt: 0.5 }}>
                {CONTACT.role}
              </Typography>
              <Typography
                sx={{ opacity: 0.9, mt: 2, maxWidth: 900, lineHeight: 1.8 }}
              >
                {PROFILE.summary}
              </Typography>

              <Stack
                direction={{ xs: "column", md: "row" }}
                spacing={2}
                flexWrap="wrap"
                useFlexGap
                sx={{ mt: 3 }}
              >
                <Typography>
                  <FontAwesomeIcon icon={faLocationDot} /> {CONTACT.location}
                </Typography>
                <Typography>
                  <FontAwesomeIcon icon={faPhone} /> {CONTACT.phoneDisplay}
                </Typography>
                <Typography>
                  <FontAwesomeIcon icon={faEnvelope} /> {CONTACT.emails[1]}
                </Typography>
                <Typography>
                  <FontAwesomeIcon icon={faGithub} /> DoshCoder
                </Typography>
              </Stack>
            </Grid>
          </Grid>
        </Box>

        <Box sx={{ p: { xs: 3, md: 4 } }}>
          <Stack
            direction="row"
            spacing={2}
            flexWrap="wrap"
            useFlexGap
            sx={{ mb: 4 }}
          >
            <Button
              variant="contained"
              component="a"
              href={CONTACT.cvView}
              target="_blank"
              rel="noopener noreferrer"
              startIcon={<FontAwesomeIcon icon={faDownload} />}
            >
              View CV
            </Button>

            <Button
              variant="outlined"
              component="a"
              href={CONTACT.cvFile}
              target="_blank"
              rel="noopener noreferrer"
              startIcon={<FontAwesomeIcon icon={faDownload} />}
            >
              Download CV
            </Button>

            <Button
              variant="outlined"
              onClick={() => window.print()}
              startIcon={<FontAwesomeIcon icon={faPrint} />}
            >
              Print
            </Button>

            <Button
              variant="outlined"
              onClick={openWhatsApp}
              startIcon={<FontAwesomeIcon icon={faWhatsapp} />}
            >
              WhatsApp
            </Button>
          </Stack>

          <Grid container spacing={3}>
            <Grid item xs={12} md={8}>
              <Card sx={{ borderRadius: 4, mb: 3 }}>
                <CardContent>
                  <Typography variant="h5" sx={{ fontWeight: 800, mb: 2 }}>
                    Career Highlights
                  </Typography>
                  <Typography color="text.secondary" sx={{ lineHeight: 1.9 }}>
                    Motivated and detail-oriented Software Engineering graduate
                    with practical experience in web development, technical
                    support, training, and digital solution delivery. Strong
                    interest in building software that improves connectivity,
                    usability, and real-world outcomes.
                  </Typography>
                </CardContent>
              </Card>

              <Card sx={{ borderRadius: 4, mb: 3 }}>
                <CardContent>
                  <Typography variant="h5" sx={{ fontWeight: 800, mb: 2 }}>
                    Work Experience
                  </Typography>

                  <Stack spacing={2.5}>
                    {EXPERIENCE.map((item) => (
                      <Box key={item.title}>
                        <Typography variant="h6" sx={{ fontWeight: 700 }}>
                          {item.title}
                        </Typography>
                        <Typography
                          color="primary.main"
                          sx={{ fontWeight: 600 }}
                        >
                          {item.company}
                        </Typography>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ mb: 1.2 }}
                        >
                          {item.type} · {item.period}
                        </Typography>

                        <Box
                          component="ul"
                          sx={{ pl: 2.5, color: "text.secondary" }}
                        >
                          {item.points.map((point) => (
                            <Typography
                              key={point}
                              component="li"
                              variant="body2"
                              sx={{ mb: 0.7, lineHeight: 1.8 }}
                            >
                              {point}
                            </Typography>
                          ))}
                        </Box>
                      </Box>
                    ))}
                  </Stack>
                </CardContent>
              </Card>

              <Card sx={{ borderRadius: 4 }}>
                <CardContent>
                  <Typography variant="h5" sx={{ fontWeight: 800, mb: 2 }}>
                    Selected Projects
                  </Typography>

                  <Stack spacing={2}>
                    {featuredProjects.map((project) => (
                      <Box key={project.id}>
                        <Typography variant="h6" sx={{ fontWeight: 700 }}>
                          {project.title}
                        </Typography>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ lineHeight: 1.8 }}
                        >
                          {project.description}
                        </Typography>
                        <Stack
                          direction="row"
                          spacing={1}
                          flexWrap="wrap"
                          useFlexGap
                          sx={{ mt: 1.2 }}
                        >
                          {project.tags.slice(0, 4).map((tag) => (
                            <Chip key={tag} label={tag} size="small" />
                          ))}
                        </Stack>
                      </Box>
                    ))}
                  </Stack>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={4}>
              <Card sx={{ borderRadius: 4, mb: 3 }}>
                <CardContent>
                  <Typography variant="h5" sx={{ fontWeight: 800, mb: 2 }}>
                    Education
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    {EDUCATION.degree}
                  </Typography>
                  <Typography color="primary.main" sx={{ fontWeight: 600 }}>
                    {EDUCATION.school}
                  </Typography>
                  <Typography color="text.secondary" sx={{ mt: 1 }}>
                    {EDUCATION.year}
                  </Typography>
                  <Typography color="text.secondary">
                    {EDUCATION.grade}
                  </Typography>

                  <Typography sx={{ fontWeight: 700, mt: 2, mb: 1 }}>
                    Relevant Coursework
                  </Typography>
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    {EDUCATION.coursework.map((item) => (
                      <Chip key={item} label={item} size="small" />
                    ))}
                  </Stack>
                </CardContent>
              </Card>

              <Card sx={{ borderRadius: 4, mb: 3 }}>
                <CardContent>
                  <Typography variant="h5" sx={{ fontWeight: 800, mb: 2 }}>
                    Technical Skills
                  </Typography>

                  <Typography sx={{ fontWeight: 700, mb: 1 }}>
                    Proficient
                  </Typography>
                  <Stack
                    direction="row"
                    spacing={1}
                    flexWrap="wrap"
                    useFlexGap
                    sx={{ mb: 2 }}
                  >
                    {SKILLS.proficient.map((item) => (
                      <Chip key={item} label={item} size="small" />
                    ))}
                  </Stack>

                  <Typography sx={{ fontWeight: 700, mb: 1 }}>
                    Exploring
                  </Typography>
                  <Stack
                    direction="row"
                    spacing={1}
                    flexWrap="wrap"
                    useFlexGap
                    sx={{ mb: 2 }}
                  >
                    {SKILLS.exploring.map((item) => (
                      <Chip
                        key={item}
                        label={item}
                        size="small"
                        variant="outlined"
                      />
                    ))}
                  </Stack>

                  <Typography sx={{ fontWeight: 700, mb: 1 }}>
                    Tools & Concepts
                  </Typography>
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    {SKILLS.tools.map((item) => (
                      <Chip key={item} label={item} size="small" />
                    ))}
                  </Stack>
                </CardContent>
              </Card>

              <Card sx={{ borderRadius: 4 }}>
                <CardContent>
                  <Typography variant="h5" sx={{ fontWeight: 800, mb: 2 }}>
                    Contact
                  </Typography>

                  <Typography sx={{ mb: 1.2 }}>
                    <strong>Phone:</strong> {CONTACT.phoneDisplay}
                  </Typography>
                  <Typography sx={{ mb: 1.2, wordBreak: "break-word" }}>
                    <strong>Email:</strong> {CONTACT.emails[1]}
                  </Typography>
                  <Typography sx={{ mb: 2 }}>
                    <strong>GitHub:</strong> DoshCoder
                  </Typography>

                  <Stack spacing={1.2}>
                    <Button
                      component="a"
                      href={`mailto:${CONTACT.emails[1]}`}
                      variant="outlined"
                    >
                      Send Email
                    </Button>
                    <Button
                      component="a"
                      href={CONTACT.github}
                      target="_blank"
                      rel="noopener noreferrer"
                      variant="outlined"
                    >
                      Open GitHub
                    </Button>
                    <Button onClick={openWhatsApp} variant="outlined">
                      WhatsApp Me
                    </Button>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Box>
      </Paper>
    </Box>
  );
};

export default Resume;
